import { useState } from 'react';
import { Typography, Box, Button, TextField, Alert, Container } from '@mui/material';
import { lookupGuest, submitRSVP, GuestGroup } from '../services/api';
import RSVPForm from '../components/RSVPForm';
import RSVPConfirmation from '../components/RSVPConfirmation';

function RSVP() {
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [guestGroups, setGuestGroups] = useState<GuestGroup[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<GuestGroup | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const nameParts = name.trim().split(' ');
      if (nameParts.length < 2) {
        throw new Error('Please enter both first and last name');
      }
      
      const firstName = nameParts[0];
      const lastName = nameParts.slice(1).join(' ');
      
      const groups = await lookupGuest(firstName, lastName);
      setGuestGroups(groups);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const [submittedRSVPs, setSubmittedRSVPs] = useState<Record<string, any> | null>(null);
  const [submittedGroup, setSubmittedGroup] = useState<GuestGroup | null>(null);

  const [groupEmail, setGroupEmail] = useState<string>('');

  const handleRSVPSubmit = async (groupId: string, rsvps: Record<string, any>, groupRSVP: { email?: string }) => {
    setError(null);
    setLoading(true);

    try {
      // Store the email for display in confirmation
      setGroupEmail(groupRSVP.email || '');
      
      // Add the email to each guest's RSVP data
      const rsvpsWithEmail = Object.entries(rsvps).reduce((acc, [guestId, guestRsvp]) => {
        acc[guestId] = {
          ...guestRsvp,
          email: groupRSVP.email
        };
        return acc;
      }, {} as Record<string, any>);
      
      await submitRSVP(groupId, rsvpsWithEmail);
      setSubmittedRSVPs(rsvpsWithEmail);
      setSubmittedGroup(selectedGroup);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit RSVP');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setGuestGroups([]);
    setSelectedGroup(null);
    setName('');
    setError(null);
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          width: '100%',
          textAlign: 'center',
          backgroundColor: '#f9f3ed',
          borderRadius: 2,
          p: { xs: 3, sm: 4 },
          mx: 'auto'
        }}
      >
        {submittedRSVPs && submittedGroup ? (
          <RSVPConfirmation
            submittedRSVPs={submittedRSVPs}
            submittedGroup={submittedGroup}
            groupEmail={groupEmail}
            onEditRSVP={() => {
              console.log("Edit Clicked")
              setSelectedGroup(submittedGroup);
              setSubmittedRSVPs(null);
              setSubmittedGroup(null);
            }}
          />
        ) : selectedGroup ? (
          <RSVPForm
            guestGroup={selectedGroup}
            onSubmit={handleRSVPSubmit}
            onBack={() => setSelectedGroup(null)}
            initialEmail={groupEmail}
          />
        ) : (
          <>
            <Typography
              variant="h3"
              component="h1"
              sx={{
                mb: 3,
                fontFamily: '"Playfair Display", serif',
                textAlign: 'center'
              }}
            >
              RSVP
            </Typography>

            <Typography variant="body1" sx={{ mb: 2, textAlign: 'center' }}>
              We're so excited that you will be able to make our wedding celebration!
            </Typography>

            <Typography variant="body1" sx={{ mb: 4, textAlign: 'center' }}>
              Please RSVP to the events you will be attending for each person in your party.
            </Typography>

            <TextField
              fullWidth
              label="First and Last name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              variant="outlined"
              sx={{ 
                mb: 1,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'white'
                }
              }}
            />

            <Typography 
              variant="body2" 
              sx={{ 
                mb: 4, 
                fontStyle: 'italic',
                color: 'text.secondary'
              }}
            >
              Ex: Bruce Gordon (not The Gordon Family or Dr. & Mrs. Fairbank)
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {guestGroups.length > 0 ? (
              <Box>
                <Typography variant="h5" sx={{ mb: 2 }}>
                  Is this you and your party?
                </Typography>
                
                {guestGroups.map(group => (
                  <Box key={group.groupId} sx={{ mb: 2 }}>
                    {group.guests.map(guest => (
                      <Typography key={guest.guestId}>
                        {guest.firstName} {guest.lastName}
                      </Typography>
                    ))}
                  </Box>
                ))}

                <Box sx={{ mt: 4, display: 'flex', gap: 2, justifyContent: 'center' }}>
                  <Button
                    variant="outlined"
                    onClick={resetForm}
                    sx={{
                      px: 4,
                      borderRadius: 25,
                    }}
                  >
                    Back
                  </Button>

                  <Button
                    variant="contained"
                    onClick={() => setSelectedGroup(guestGroups[0])}
                    sx={{
                      px: 4,
                      borderRadius: 25,
                      backgroundColor: 'rgba(175, 99, 42, 1)',
                      color: '#fff',
                      '&:hover': {
                        backgroundColor: 'rgba(187, 138, 101, 1)',
                      },
                    }}
                  >
                    Next
                  </Button>
                </Box>
              </Box>
            ) : (
              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={loading}
                sx={{
                  px: 8,
                  py: 1.5,
                  borderRadius: 25,
                  fontFamily: '"Open Sans", sans-serif',
                  backgroundColor: 'rgba(175, 99, 42, 1)',
                  color: '#fff',
                  '&:hover': {
                    backgroundColor: 'rgba(187, 138, 101, 1)',
                  },
                }}
              >
                {loading ? 'Loading...' : 'RSVP'}
              </Button>
            )}
          </>
        )}
      </Box>
    </Container>
  );
}

export default RSVP;
